import {
    Box,
    Button,
    Card,
    CardContent,
    Drawer,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Children, Fragment, isValidElement, useState } from 'react';

// Grid de 2 colunas dos campos no mobile. Neutraliza larguras fixas dos campos
// (datas 150px, selects minWidth, wrappers `flex: 0 0 Npx`) para preencherem a
// célula; `data-wide` ocupa a linha inteira (campos longos).
const fieldsGridSx = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 1.5,
    alignContent: 'start',
    '& > *': {
        minWidth: '0 !important',
        width: 'auto !important',
        flex: 'none !important',
    },
    '& > [data-wide]': { gridColumn: '1 / -1' },
    '& .MuiFormControl-root, & .MuiTextField-root, & .MuiAutocomplete-root, & .MuiPickersTextField-root': {
        minWidth: '0 !important',
        width: '100% !important',
    },
    '& .MuiPickersOutlinedInput-root': { width: '100% !important' },
};

/**
 * Toolbar de filtros das listas/relatórios (DESIGN-SYSTEM.md §6.1 `report-toolbar`).
 *
 * - Tablet/desktop (>= `sm`): renderiza exatamente o mesmo Card + flex-wrap que
 *   cada tela já tinha — `primary`, `children` e `actions` na mesma ordem.
 * - Mobile (< `sm`): barra compacta com o campo principal (`primary`, em geral a
 *   busca) + botão "Filtros" com badge de filtros ativos. Os demais campos
 *   (`children`) abrem num bottom sheet, em grid de 2 colunas (campos curtos) —
 *   marque um filho com `data-wide` para ocupar a linha toda — e "Limpar"/
 *   "Aplicar" ficam lado a lado, fixos no rodapé do sheet.
 *
 * Props:
 * - `primary`      campo principal (busca). Fica sempre visível no mobile.
 * - `primaryIndex` só desktop: posição de `primary` entre os `children`
 *                  (0 = antes de todos; omitido = depois de todos).
 * - `children`     campos secundários (recolhidos no mobile).
 * - `actions`      botões Limpar/Buscar de hoje (só desktop; spacer incluso).
 * - `activeCount`  nº de filtros ativos (badge do botão "Filtros").
 * - `onClear`/`onApply`  handlers existentes da tela (mobile).
 * - `applyLabel`   texto do botão de aplicar no mobile (padrão "Aplicar").
 * - `applyDisabled` desabilita o aplicar (mobile: inline e sheet).
 * - `collapse`     override explícito. Omitido = automático: no mobile, mais de 2
 *                  campos (`primary` + filhos, sem separadores Typography) recolhe
 *                  atrás do botão "Filtros"; 2 ou menos fica inline (grid de 2
 *                  colunas direto no card). `false` força inline, `true` recolhe.
 * - `mobileAction` só mobile (modo recolhido): `{ label, onClick, disabled }`.
 *                  Botão contido (ex.: "Gerar") ao lado do "Filtros", ambos compactos (36px, largura do conteúdo, à esquerda); a busca
 *                  (`primary`) passa para uma linha própria acima. Opcional.
 * - `sx`/`contentSx`  overrides do Card/CardContent no desktop (e Card no mobile).
 * - `plain`       opt-in (padrão false = card atual): faixa de filtros sem card,
 *                  só com linha inferior (padrão FilterBar dos protótipos).
 */
export default function FilterToolbar({
    primary,
    primaryIndex,
    children,
    actions,
    activeCount = 0,
    onClear,
    onApply,
    applyLabel = 'Aplicar',
    applyDisabled = false,
    collapse,
    mobileAction,
    sx,
    contentSx,
    plain = false,
}) {
    const theme = useTheme();
    // `plain` (opt-in): faixa de filtros SEM card (FilterBar dos protótipos de
    // relatório) — sem fundo/borda/sombra, só uma linha inferior separando da lista.
    const Shell = plain ? Box : Card;
    const Body = plain ? Box : CardContent;
    const shellSx = (cardSx) => (plain
        ? { mb: '20px', pb: '18px', borderBottom: '1px solid', borderColor: 'divider', ...sx }
        : cardSx);
    const bodySx = (cardSx) => (plain ? { ...cardSx, p: 0, '&:last-child': { pb: 0 } } : cardSx);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(false);

    const secondaryItems = Children.toArray(children);
    const hasSecondary = secondaryItems.length > 0;

    // Regra do mobile: mais de 2 campos de filtro => recolhe atrás do botão
    // "Filtros"; 2 ou menos => campos inline. `collapse` explícito vence. Não conta
    // separadores textuais (ex.: "até" em Typography) nem filhos nulos.
    const fieldCount = (primary ? 1 : 0)
        + secondaryItems.filter((item) => isValidElement(item) && item.type !== Typography).length;
    const shouldCollapse = collapse === undefined ? fieldCount > 2 : collapse;

    if (!isMobile) {
        // Mantém a posição original do campo principal no desktop.
        const index = primaryIndex === undefined ? secondaryItems.length : primaryIndex;
        const desktopItems = [
            ...secondaryItems.slice(0, index),
            ...(primary ? [<Fragment key="__filter-primary">{primary}</Fragment>] : []),
            ...secondaryItems.slice(index),
        ];

        return (
            <Shell sx={shellSx({ mb: 2, ...sx })}>
                <Body sx={bodySx({
                    p: 2,
                    '&:last-child': { pb: 2 },
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    gap: plain ? '14px 22px' : 2,
                    ...contentSx,
                })}>
                    {desktopItems}
                    {actions && (
                        <>
                            <Box sx={{ flex: '1 1 auto' }} />
                            {actions}
                        </>
                    )}
                </Body>
            </Shell>
        );
    }

    // Poucos campos (ex.: só período): sem recolher, grid de 2 colunas direto no
    // card + Limpar/Aplicar lado a lado.
    if (!shouldCollapse) {
        return (
            <Shell sx={shellSx({ mb: 1.5 })}>
                <Body sx={bodySx({ p: 1.5, '&:last-child': { pb: 1.5 }, ...fieldsGridSx })}>
                    {primary && <Box data-wide>{primary}</Box>}
                    {children}
                    {(onClear || onApply || mobileAction) && (
                    <Box data-wide sx={{ display: 'flex', gap: 1 }}>
                        {onClear && (
                            <Button
                                variant="outlined"
                                onClick={onClear}
                                sx={mobileAction
                                    ? { height: 36, minHeight: 36, px: 2, flex: '0 0 auto', borderRadius: '9px', fontSize: '13px' }
                                    : { flex: 1, height: 40 }}
                            >
                                Limpar
                            </Button>
                        )}
                        {mobileAction && (
                            <Button
                                variant="contained"
                                onClick={mobileAction.onClick}
                                disabled={mobileAction.disabled}
                                sx={{ height: 36, minHeight: 36, px: 2, flex: '0 0 auto', borderRadius: '9px', fontSize: '13px' }}
                            >
                                {mobileAction.label}
                            </Button>
                        )}
                        {onApply && !mobileAction && (
                            <Button variant="contained" onClick={onApply} disabled={applyDisabled} sx={{ flex: 1, height: 40 }}>
                                {applyLabel}
                            </Button>
                        )}
                    </Box>
                    )}
                </Body>
            </Shell>
        );
    }

    const handleApply = () => {
        if (onApply) onApply();
        setOpen(false);
    };

    const handleClear = () => {
        if (onClear) onClear();
    };

    return (
        <>
            <Shell sx={shellSx({ mb: 1.5 })}>
                <Body sx={bodySx({
                    p: 1.25,
                    '&:last-child': { pb: 1.25 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    ...(mobileAction ? { flexWrap: 'wrap', columnGap: 1, rowGap: 0.75 } : {}),
                })}>
                    {primary && (
                        <Box sx={{
                            flex: mobileAction ? '1 1 100%' : '1 1 auto',
                            minWidth: 0,
                            // Compacta: sem o micro-label acima do campo, largura fluida.
                            '& > *': { flex: '1 1 auto !important', width: '100% !important', minWidth: '0 !important' },
                            '& > * > p:first-of-type': { display: 'none' },
                        }}>
                            {primary}
                        </Box>
                    )}

                    {hasSecondary ? (
                        <Button
                            variant="outlined"
                            onClick={() => setOpen(true)}
                            startIcon={<SlidersHorizontal size={mobileAction ? 16 : 15} strokeWidth={2} />}
                            sx={{
                                height: mobileAction ? 36 : 40,
                                minWidth: 40,
                                flexShrink: 0,
                                px: mobileAction ? 1.25 : 1.5,
                                borderRadius: '9px',
                                fontSize: '13px',
                                ...(mobileAction ? { flex: '0 0 auto' } : primary ? {} : { flex: '1 1 auto' }),
                            }}
                        >
                            Filtros
                            {activeCount > 0 && (
                                <Box
                                    component="span"
                                    sx={{
                                        ml: 0.75,
                                        minWidth: 18,
                                        height: 18,
                                        px: '5px',
                                        borderRadius: '9px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        lineHeight: 1,
                                        color: '#fff',
                                        bgcolor: 'primary.main',
                                    }}
                                >
                                    {activeCount}
                                </Box>
                            )}
                        </Button>
                    ) : (
                        onApply && !mobileAction && (
                            <IconButton
                                aria-label="Buscar"
                                onClick={onApply}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    flexShrink: 0,
                                    borderRadius: '9px',
                                    color: '#fff',
                                    bgcolor: 'primary.main',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                }}
                            >
                                <Search size={16} strokeWidth={2} />
                            </IconButton>
                        )
                    )}

                    {mobileAction && (
                        <Button
                            variant="contained"
                            onClick={mobileAction.onClick}
                            disabled={mobileAction.disabled}
                            sx={{ height: 36, minHeight: 36, flex: '0 0 auto', minWidth: 0, px: 2, borderRadius: '9px', fontSize: '13px' }}
                        >
                            {mobileAction.label}
                        </Button>
                    )}
                </Body>
            </Shell>

            {hasSecondary && (
                <Drawer
                    anchor="bottom"
                    open={open}
                    onClose={() => setOpen(false)}
                    PaperProps={{
                        sx: {
                            maxHeight: '88vh',
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ px: 2, pt: 1.5, pb: 1, flexShrink: 0 }}
                    >
                        <Typography sx={{ fontSize: '15px', fontWeight: 800, letterSpacing: '-0.01em' }}>
                            Filtros
                        </Typography>
                        <IconButton aria-label="Fechar filtros" onClick={() => setOpen(false)} sx={{ width: 36, height: 36 }}>
                            <X size={18} strokeWidth={2} />
                        </IconButton>
                    </Stack>

                    <Box sx={{ px: 2, pb: 2, overflowY: 'auto', flex: '1 1 auto', ...fieldsGridSx }}>
                        {children}
                    </Box>

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            p: 2,
                            flexShrink: 0,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                        }}
                    >
                        {onClear && (
                            <Button variant="outlined" onClick={handleClear} sx={{ flex: 1, height: 40 }}>
                                Limpar
                            </Button>
                        )}
                        <Button variant="contained" onClick={handleApply} disabled={onApply ? applyDisabled : false} sx={{ flex: 1, height: 40 }}>
                            {onApply ? (mobileAction ? 'Aplicar' : applyLabel) : 'Concluir'}
                        </Button>
                    </Stack>
                </Drawer>
            )}
        </>
    );
}

/**
 * Linha de chips de filtros ativos/contadores: em mobile fica numa única linha
 * com scroll horizontal; em tablet/desktop quebra linha como antes.
 */
export function FilterChips({ children, sx }) {
    return (
        <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{
                mb: { xs: 1.5, sm: 2 },
                rowGap: 1,
                flexWrap: { xs: 'nowrap', sm: 'wrap' },
                overflowX: { xs: 'auto', sm: 'visible' },
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                '& > *': { flexShrink: { xs: 0, sm: 1 } },
                ...sx,
            }}
        >
            {children}
        </Stack>
    );
}
